<template>
  <Layout title="Invoices" description="Manage all generated invoices">
    <div class="container-fluid py-4">
      <!-- HEADER -->
      <!-- <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 class="fw-bold mb-1">Invoices</h1>
          <p class="text-muted mb-0">Manage all generated invoices</p>
        </div>

        <router-link
          to="/invoice/create"
          class="btn btn-primary btn-sm"
        >
          + Create Invoice
        </router-link>
      </div> -->

      <!-- TABLE CARD -->
      <div class="bg-white border rounded-4 shadow-sm p-4">
        <!-- LOADING -->
        <div v-if="loading" class="text-center py-5 text-muted">
          Loading invoices...
        </div>

        <!-- EMPTY -->
        <div v-else-if="invoices.length === 0" class="text-center py-5 text-muted">
          No invoices found
        </div>

        <!-- TABLE -->
        <div v-else class="table-responsive">
          <table class="table align-middle">
            <thead class="table-light">
              <tr>
                <th>Invoice No</th>
                <th>Date</th>
                <th>Vessel</th>
                <th>Bill To</th>
                <th>Total</th>
                <th>Updated</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="invoice in invoices" :key="invoice.id">
                <td class="fw-semibold">{{ invoice.invoiceNo }}</td>
                <td>{{ formatDate(invoice.date) }}</td>
                <td>{{ invoice.vessel || '-' }}</td>
                <td>{{ getBillTo(invoice.billTo) }}</td>
                <td>$ {{ formatMoney(invoice.totalAmount) }}</td>
                <td>{{ formatDateTime(invoice.updatedAt) }}</td>
                <td class="text-end">
                  <div class="d-flex justify-content-end gap-2 flex-wrap">
                    <button
                      class="btn btn-outline-success btn-sm"
                      @click="downloadInvoice(invoice.id, invoice.invoiceNo)"
                    >
                      Download
                    </button>

                    <button
                      class="btn btn-outline-primary btn-sm"
                      @click="goEdit(invoice.id)"
                    >
                      Edit
                    </button>

                    <button
                      class="btn btn-outline-danger btn-sm"
                      @click="openDeleteModal(invoice)"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- PAGINATION -->
        <div
          v-if="totalPages > 1"
          class="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2"
        >
          <div class="text-muted small">
            Page {{ page }} of {{ totalPages }} • {{ total }} invoices
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page <= 1"
              @click="changePage(page - 1)"
            >
              ← Prev
            </button>

            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="page >= totalPages"
              @click="changePage(page + 1)"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- DELETE MODAL -->
    <div
      v-if="showDeleteModal"
      class="delete-modal-backdrop"
      @click.self="closeDeleteModal"
    >
      <div class="delete-modal card border-0 shadow-lg">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0 fw-bold text-danger">Delete Invoice</h5>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="closeDeleteModal"
            :disabled="deleting"
          >
            ✕
          </button>
        </div>

        <div class="card-body">
          <p class="mb-2">
            Are you sure you want to delete this invoice?
          </p>

          <div class="border rounded p-3 bg-light">
            <div><strong>Invoice No:</strong> {{ selectedInvoiceToDelete ? selectedInvoiceToDelete.invoiceNo : '-' }}</div>
            <div><strong>Date:</strong> {{ selectedInvoiceToDelete ? formatDate(selectedInvoiceToDelete.date) : '-' }}</div>
            <div><strong>Vessel:</strong> {{ selectedInvoiceToDelete ? (selectedInvoiceToDelete.vessel || '-') : '-' }}</div>
            <div><strong>Total:</strong> $ {{ selectedInvoiceToDelete ? formatMoney(selectedInvoiceToDelete.totalAmount) : '0.00' }}</div>
          </div>

          <p class="text-danger small mt-3 mb-0">
            This action cannot be undone.
          </p>
        </div>

        <div class="card-footer d-flex justify-content-end gap-2">
          <button
            class="btn btn-outline-secondary"
            @click="closeDeleteModal"
            :disabled="deleting"
          >
            Cancel
          </button>

          <button
            class="btn btn-danger"
            @click="deleteInvoiceConfirmed"
            :disabled="deleting"
          >
            {{ deleting ? 'Deleting...' : 'Delete Invoice' }}
          </button>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import axios from 'axios'
import { useToast } from 'vue-toastification'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import router from '@/router'

export default {
  name: 'InvoiceListPage',
  components: { Layout },

  setup() {
    return { toast: useToast() }
  },

  data() {
    return {
      token: null,
      loading: false,
      invoices: [],
      page: 1,
      pageSize:10,
      total: 0,
      totalPages: 1,
      showDeleteModal: false,
      selectedInvoiceToDelete: null,
      deleting: false
    }
  },

  async mounted() {
    await this.checkAuthState()
    await this.fetchInvoices()
  },

  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')
      const token = await user.getIdToken()
      return { Authorization: token }
    },

    async checkAuthState() {
      const auth = getAuth()
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async user => {
          if (user) {
            this.token = await user.getIdToken()
            resolve()
          } else {
            router.push('/')
            reject(new Error('No user logged in'))
          }
        })
      })
    },

    async fetchInvoices() {
      this.loading = true
      try {
        const headers = await this.getAuthHeader()

        const res = await axios.get(
          `${this.$apiURL}/api/invoices`,
          {
            headers,
            params: {
              page: this.page,
              pageSize: this.pageSize
            }
          }
        )

        this.invoices = (res.data && res.data.invoices) ? res.data.invoices : []
        this.total = (res.data && res.data.total) ? res.data.total : 0
        this.totalPages = (res.data && res.data.totalPages) ? res.data.totalPages : 1
      } catch (err) {
        console.error(err)
        this.toast.error('Failed to load invoices')
        this.invoices = []
      } finally {
        this.loading = false
      }
    },

    changePage(newPage) {
      if (newPage < 1 || newPage > this.totalPages) return
      this.page = newPage
      this.fetchInvoices()
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      try {
        return new Date(dateStr).toLocaleString('en-SG', { hour12: false })
      } catch (e) {
        return dateStr
      }
    },

    formatMoney(val) {
      const num = Number(val || 0)
      return num.toFixed(2)
    },

    getBillTo(billTo) {
      if (!billTo) return '-'
      return billTo.line1 || '-'
    },

    async downloadInvoice(invoiceId, invoiceNo) {
      try {
        const headers = await this.getAuthHeader()

        const res = await axios.get(
          `${this.$apiURL}/api/invoices/download/${invoiceId}`,
          {
            headers,
            responseType: 'blob'
          }
        )

        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${invoiceNo || invoiceId}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()

        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error(err)
        this.toast.error('Failed to download invoice')
      }
    },

    goEdit(invoiceId) {
      this.$router.push(`/invoice/edit/${invoiceId}`)
    },

    openDeleteModal(invoice) {
      this.selectedInvoiceToDelete = invoice
      this.showDeleteModal = true
    },

    closeDeleteModal() {
      if (this.deleting) return
      this.showDeleteModal = false
      this.selectedInvoiceToDelete = null
    },

    async deleteInvoiceConfirmed() {
      if (!this.selectedInvoiceToDelete || !this.selectedInvoiceToDelete.id) {
        return
      }

      this.deleting = true

      try {
        const headers = await this.getAuthHeader()

        await axios.delete(
          `${this.$apiURL}/api/invoices/${this.selectedInvoiceToDelete.id}`,
          { headers }
        )

        this.toast.success(`Invoice ${this.selectedInvoiceToDelete.invoiceNo} deleted`)

        this.closeDeleteModal()

        // if last item on page deleted, go previous page if possible
        if (this.invoices.length === 1 && this.page > 1) {
          this.page--
        }

        await this.fetchInvoices()
      } catch (err) {
        console.error(err)
        this.toast.error('Failed to delete invoice')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
<style scoped>
.delete-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 99991;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.delete-modal {
  width: 100%;
  max-width: 520px;
  border-radius: 18px;
  overflow: hidden;
}
</style>